import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Filter,
  Grid,
  Circle,
  FolderTree,
  SlidersHorizontal,
  Package,
  ShoppingBag,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Heart,
  Loader2,
  AlertCircle,
  RefreshCw,
  Utensils,
  SprayCan,
  X,
  Star,
  TrendingUp,
  Clock,
  Eye,
} from "lucide-react";
import { productService } from "../api/services/productService";
import { useToast } from "../components/common/ThemeToaster";
import { useWishlist } from "../context/WishlistContext";

// Helper function to get full image URL
const getFullImageUrl = (path) => {
  if (!path) return null;

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  if (path.startsWith("/uploads")) {
    const apiBaseUrl =
      import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
    return `${apiBaseUrl}${path}`;
  }

  return path;
};

// ========== IMAGE SLIDER COMPONENT ==========
const ImageSlider = ({ images, alt, autoPlay = true, interval = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState({});

  useEffect(() => {
    if (!autoPlay || !images || images.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [images, autoPlay, interval]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevious = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleImageError = (index) => {
    setImageError((prev) => ({ ...prev, [index]: true }));
  };

  if (!images || images.length === 0) return null;

  return (
    <div className="relative w-full h-full group/slider">
      {/* Images */}
      <div className="relative w-full h-full overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            src={images[currentIndex]}
            alt={`${alt} - Image ${currentIndex + 1}`}
            className="w-full h-full object-cover"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
            onError={() => handleImageError(currentIndex)}
          />
        </AnimatePresence>

        {/* Fallback if image errors */}
        {imageError[currentIndex] && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
            <Package size={48} className="text-gray-400" />
          </div>
        )}
      </div>

      {/* Navigation Arrows - Only show if more than 1 image */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-full opacity-0 group-hover/slider:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-900 shadow-lg z-10"
          >
            <ChevronLeft
              size={16}
              className="text-gray-700 dark:text-gray-300"
            />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-full opacity-0 group-hover/slider:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-900 shadow-lg z-10"
          >
            <ChevronRight
              size={16}
              className="text-gray-700 dark:text-gray-300"
            />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goToSlide(index);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? "bg-white w-6 shadow-lg"
                  : "bg-white/60 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      )}

      {/* Image Counter */}
      {images.length > 1 && (
        <div className="absolute top-3 right-3 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs font-medium z-10">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

// Helper function to extract all images from a product
const extractProductImages = (product) => {
  const images = [];

  // Add main image if exists
  if (product.mainImagePath) {
    images.push(getFullImageUrl(product.mainImagePath));
  }

  // Add gallery images if they exist
  if (product.galleryImages && product.galleryImages.length > 0) {
    product.galleryImages.forEach((img) => {
      if (img.path) {
        images.push(getFullImageUrl(img.path));
      } else if (img.data) {
        images.push(`data:${img.mimeType};base64,${img.data}`);
      }
    });
  }

  // Add single image URL if exists
  if (product.image && images.length === 0) {
    images.push(product.image);
  }

  return images;
};

// ========== PRODUCT CARD COMPONENT ==========
const ProductCard = ({ product }) => {
  const isAvailable = product.isAvailable === true || product.isAvailable === 1;
  const isOnSale = product.isOnSale === true || product.isOnSale === 1;
  const isNew = product.isNew === true || product.isNew === 1;

  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { wishlist: toastWishlist } = useToast();
  const isWishlisted = isInWishlist(product.id);

  const [productImages, setProductImages] = useState([]);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const images = extractProductImages(product);
    setProductImages(images);
    setImageLoading(false);
  }, [product]);

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isWishlisted) {
      removeFromWishlist(product.id);
      toastWishlist(`Removed "${product.name}" from wishlist`);
    } else {
      addToWishlist(product);
      toastWishlist(`Added "${product.name}" to wishlist`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className="group bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
        {imageLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 size={32} className="animate-spin text-primary-500" />
          </div>
        ) : productImages.length > 1 ? (
          // Use slider if multiple images
          <ImageSlider
            images={productImages}
            alt={product.imageAlt || product.name}
          />
        ) : productImages.length === 1 ? (
          // Single image
          <img
            src={productImages[0]}
            alt={product.imageAlt || product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            onError={() => {
              console.error(`Failed to load image: ${productImages[0]}`);
              setImageError(true);
            }}
          />
        ) : imageError ? (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={48} className="text-gray-400" />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={48} className="text-gray-400" />
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
          {isOnSale && (
            <span className="px-2 py-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-lg shadow-lg">
              SALE
            </span>
          )}
          {isNew && (
            <span className="px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-lg shadow-lg">
              NEW
            </span>
          )}
          {!isAvailable && (
            <span className="px-2 py-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white text-xs font-bold rounded-lg shadow-lg">
              OUT OF STOCK
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={handleWishlistToggle}
          className="absolute top-3 right-3 p-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl hover:bg-primary-600 hover:text-white transition-all shadow-lg z-10 group/btn"
        >
          <Heart
            size={16}
            className={`transition-all ${
              isWishlisted
                ? "fill-red-500 text-red-500"
                : "text-gray-500 group-hover/btn:text-white"
            }`}
          />
        </button>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1 text-base">
              {product.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {product.category === "food"
                ? "Food & Beverages"
                : "Non-Food Products"}{" "}
              •{" "}
              {product.createdAt
                ? new Date(product.createdAt).toLocaleDateString()
                : "Unknown"}
            </p>
          </div>
          {/* {isAvailable && (
            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs font-medium rounded-full">
              In Stock
            </span>
          )} */}
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
          {product.shortDescription ||
            product.description ||
            "No description available"}
        </p>

        <div className="flex items-center justify-between pt-3 gap-2 border-t border-gray-100 dark:border-gray-800">
          <Link
            to={`/products/${product.slug}`}
            className="flex-1 text-center py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white text-sm font-medium rounded-xl transition-all"
          >
            View Details
          </Link>
          <Link
            to={`/inquiry?productId=${product.id}`}
            className="p-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-xl transition-all shadow-md hover:shadow-lg"
          >
            <ShoppingBag size={18} />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

// ========== CIRCULAR LIST VIEW COMPONENT ==========
const ProductCircularCard = ({ product }) => {
  const isAvailable = product.isAvailable === true || product.isAvailable === 1;
  const isOnSale = product.isOnSale === true || product.isOnSale === 1;
  const isNew = product.isNew === true || product.isNew === 1;
  const isPopular = product.isPopular === true || product.isPopular === 1;

  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { wishlist: toastWishlist } = useToast();
  const isWishlisted = isInWishlist(product.id);

  const [productImages, setProductImages] = useState([]);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const images = extractProductImages(product);
    setProductImages(images);
    setImageLoading(false);
  }, [product]);

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isWishlisted) {
      removeFromWishlist(product.id);
      toastWishlist(`Removed "${product.name}" from wishlist`);
    } else {
      addToWishlist(product);
      toastWishlist(`Added "${product.name}" to wishlist`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className="group bg-gradient-to-br from-white via-white/95 to-gray-50/90 dark:from-gray-900/90 dark:via-gray-900/85 dark:to-gray-800/80 backdrop-blur-sm rounded-3xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden hover:shadow-2xl hover:border-primary-500/30 transition-all duration-500"
    >
      <div className="flex flex-col md:flex-row">
        {/* Circular Image Section */}
        <div className="md:w-1/3 lg:w-1/4 relative overflow-hidden bg-gradient-to-br from-primary-500/5 via-secondary-500/5 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-8 md:p-6">
          <div className="relative aspect-square rounded-full overflow-hidden ring-4 ring-white/50 dark:ring-gray-800/50 shadow-xl group-hover:ring-primary-500/30 transition-all duration-300">
            {imageLoading ? (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
                <Loader2 size={32} className="animate-spin text-primary-500" />
              </div>
            ) : productImages.length > 1 ? (
              // Use slider for multiple images in circular view
              <div className="w-full h-full">
                <ImageSlider
                  images={productImages}
                  alt={product.imageAlt || product.name}
                  autoPlay={true}
                  interval={4000}
                />
              </div>
            ) : productImages.length === 1 ? (
              <img
                src={productImages[0]}
                alt={product.imageAlt || product.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                onError={() => {
                  console.error(
                    `Failed to load circular card image: ${productImages[0]}`,
                  );
                  setImageError(true);
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
                <Package size={48} className="text-gray-400" />
              </div>
            )}

            {/* Badges on circular image */}
            <div className="absolute top-2 left-14 flex flex-col gap-1.5 z-10">
              {isOnSale && (
                <span className="px-2.5 py-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                  SALE
                </span>
              )}
              {isNew && (
                <span className="px-2.5 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-full shadow-lg">
                  NEW
                </span>
              )}
              {isPopular && (
                <span className="px-2.5 py-1 bg-gradient-to-r from-orange-500 to-amber-600 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
                  <TrendingUp size={12} />
                  POPULAR
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 p-6 md:p-8">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="flex-1">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                {product.name}
              </h3>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                  {product.category === "food"
                    ? "Food & Beverages"
                    : "Non-Food Products"}
                </span>
                {product.createdAt && (
                  <span className="flex items-center gap-1.5">
                    <Clock size={12} />
                    {new Date(product.createdAt).toLocaleDateString()}
                  </span>
                )}
                {/* {product.sku && (
                  <span className="flex items-center gap-1.5">
                    <Package size={12} />
                    SKU: {product.sku}
                  </span>
                )} */}
              </div>
            </div>

            {/* {isAvailable ? (
              <span className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold rounded-full flex items-center gap-1.5 shadow-sm">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                In Stock
              </span>
            ) : (
              <span className="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-semibold rounded-full shadow-sm">
                Out of Stock
              </span>
            )} */}
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className={`${
                      i < Math.floor(product.rating || 4.5)
                        ? "fill-yellow-400 text-yellow-400"
                        : "fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {product.rating || "4.5"}
              </span>
              <span className="text-xs text-gray-500">
                ({product.reviewsCount || "128"} reviews)
              </span>
            </div>
            {product.viewCount && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Eye size={12} />
                <span>{product.viewCount.toLocaleString()} views</span>
              </div>
            )}
          </div>

          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4 line-clamp-2">
            {product.description ||
              product.shortDescription ||
              "No description available"}
          </p>

          {product.features && product.features.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {product.features.slice(0, 3).map((feature, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 bg-gray-100 dark:bg-gray-800 text-xs rounded-full text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                >
                  {feature}
                </span>
              ))}
              {product.features.length > 3 && (
                <span className="px-2.5 py-1 bg-gray-100 dark:bg-gray-800 text-xs rounded-full text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                  +{product.features.length - 3} more
                </span>
              )}
            </div>
          )}

          {product.specifications &&
            Object.keys(product.specifications).length > 0 && (
              <div className="mb-5 pb-4 border-b border-gray-100 dark:border-gray-800">
                <div className="flex flex-wrap gap-3 text-sm">
                  {Object.entries(product.specifications)
                    .slice(0, 2)
                    .map(([key, value]) => (
                      <div key={key} className="flex items-center gap-1">
                        <span className="font-medium text-gray-600 dark:text-gray-400 capitalize">
                          {key}:
                        </span>
                        <span className="text-gray-700 dark:text-gray-300">
                          {String(value)}
                        </span>
                      </div>
                    ))}
                  {Object.keys(product.specifications).length > 2 && (
                    <span className="text-gray-500 text-xs">
                      +{Object.keys(product.specifications).length - 2} more
                      specs
                    </span>
                  )}
                </div>
              </div>
            )}

          {/* Action Buttons with Wishlist Button moved here */}
          <div className="flex items-center gap-3">
            <Link
              to={`/products/${product.slug}`}
              className="flex-1 text-center py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white text-sm font-semibold rounded-xl transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
            >
              View Details
            </Link>

            {/* Wishlist Button - Now positioned in content section */}
            <button
              onClick={handleWishlistToggle}
              className={`p-3 rounded-full transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02] border ${
                isWishlisted
                  ? "bg-red-50 dark:bg-red-900/30 text-red-500 border-red-200 dark:border-red-800"
                  : "bg-white dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700 hover:text-red-500 hover:border-red-200 dark:hover:border-red-800"
              }`}
              title={isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
            >
              <Heart size={18} className={isWishlisted ? "fill-current" : ""} />
            </button>

            <Link
              to={`/inquiry?productId=${product.id}`}
              className="p-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-full transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02] group/inquiry"
            >
              <ShoppingBag
                size={18}
                className="group-hover/inquiry:rotate-12 transition-transform"
              />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ========== MAIN PRODUCTS COMPONENT ==========
// Update the Products component - replace the existing state and fetchProducts

const Products = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [viewMode, setViewMode] = useState("grid");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(true);

  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [productsPerPage, setProductsPerPage] = useState(9);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { success: toastSuccess, error: toastError } = useToast();

  const categoryOptions = [
    { id: "food", name: "Food & Beverages", icon: Utensils },
    { id: "non-food", name: "Non-Food Products", icon: SprayCan },
  ];

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, sortBy, currentPage, searchQuery, productsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery, sortBy]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: productsPerPage,
      };

      if (searchQuery) params.search = searchQuery;
      if (selectedCategory) params.category = selectedCategory;

      if (sortBy === "popular") {
        params.sortBy = "isFeatured";
        params.sortOrder = "DESC";
      } else if (sortBy === "name") {
        params.sortBy = "name";
        params.sortOrder = "ASC";
      } else if (sortBy === "newest") {
        params.sortBy = "createdAt";
        params.sortOrder = "DESC";
      }

      const response = await productService.getAllProducts(params);

      let productsData = [];
      let paginationData = null;

      if (response?.data?.data?.products) {
        productsData = response.data.data.products;
        paginationData = response.data.data.pagination;
      } else if (response?.data?.products) {
        productsData = response.data.products;
        paginationData = response.data.pagination;
      } else if (response?.products) {
        productsData = response.products;
        paginationData = response.pagination;
      } else if (Array.isArray(response?.data?.data)) {
        productsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        productsData = response.data;
      } else if (Array.isArray(response)) {
        productsData = response;
      } else {
        productsData = [];
      }

      // Ensure productsData is always an array
      if (!Array.isArray(productsData)) {
        productsData = [];
      }

      setProducts(productsData);

      // Set pagination
      if (paginationData) {
        const pages = paginationData.totalPages || paginationData.pages || 1;
        const total =
          paginationData.total || paginationData.count || productsData.length;

        setTotalPages(pages);
        setTotalProducts(total);
      } else {
        // Fallback: client-side pagination
        const startIndex = (currentPage - 1) * productsPerPage;
        const endIndex = startIndex + productsPerPage;
        const paginatedProducts = productsData.slice(startIndex, endIndex);

        setProducts(paginatedProducts);
        setTotalPages(Math.ceil(productsData.length / productsPerPage) || 1);
        setTotalProducts(productsData.length);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(err.message || "Failed to load products");
      toastError(err.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setSortBy("popular");
    setCurrentPage(1);
  };

  const activeFiltersCount = [searchQuery, selectedCategory].filter(
    Boolean,
  ).length;

  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 flex items-center justify-center border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
        <div className="text-center">
          <div className="relative">
            <Loader2 className="relative w-16 h-16 animate-spin text-primary-600 dark:text-primary-400 mx-auto mb-4" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            Loading products...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 pb-12 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 shadow-sm mb-4 sm:mb-6">
              <Package className="w-3 h-3 sm:w-4 sm:h-4 text-primary-500" />
              <span className="text-sm font-medium bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                Discover {totalProducts}+ Premium Products
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight">
              <span className="text-gray-900 dark:text-white">Wholesale</span>
              <br />
              <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Marketplace
              </span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              Source quality products directly from verified suppliers.
              Competitive pricing, bulk discounts, and global shipping.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="pb-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="relative"
          >
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg p-2 sm:p-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                {/* Search Input Container - Full width on mobile */}
                <div className="flex-1 flex items-center px-3 sm:px-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 transition-all">
                  <Search
                    size={18}
                    className="text-gray-400 mr-2 sm:mr-3 flex-shrink-0"
                  />
                  <input
                    type="text"
                    placeholder="Search products by name or category..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={(e) => {
                      // Add focus styles to parent container
                      e.target
                        .closest(".flex-1")
                        ?.classList.add(
                          "ring-2",
                          "ring-primary-500/20",
                          "border-primary-500",
                        );
                    }}
                    onBlur={(e) => {
                      // Remove focus styles from parent container
                      e.target
                        .closest(".flex-1")
                        ?.classList.remove(
                          "ring-2",
                          "ring-primary-500/20",
                          "border-primary-500",
                        );
                    }}
                    className="w-full py-3 sm:py-4 bg-transparent border-none focus:outline-none text-gray-900 dark:text-white placeholder-gray-400 text-sm sm:text-base"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
                    >
                      <X size={16} className="text-gray-400" />
                    </button>
                  )}
                </div>

                {/* Action Buttons Container */}
                <div className="flex items-center gap-2 sm:gap-3 justify-end flex-shrink-0">
                  {/* Circular Expressive Filter Icon - Desktop */}
                  <button
                    onClick={() => setIsFilterVisible(!isFilterVisible)}
                    className="hidden lg:flex relative items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 group"
                    title="Toggle Categories"
                  >
                    <SlidersHorizontal size={18} className="relative z-10" />
                    <span className="absolute inset-0 rounded-full bg-primary-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                    <span className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 opacity-0 group-hover:opacity-30 blur-md transition-opacity duration-300" />
                    {activeFiltersCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>

                  {/* Mobile filter button - Full width on mobile */}
                  <button
                    onClick={() => setShowMobileFilters(true)}
                    className="lg:hidden flex-1 sm:flex-none relative flex items-center justify-center gap-2 px-4 py-3 sm:py-2.5 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <Filter size={16} />
                    <span className="text-sm font-medium">Categories</span>
                    {activeFiltersCount > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-white/20 text-xs rounded-full">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>

                  {/* Refresh button - Circular */}
                  <button
                    onClick={fetchProducts}
                    className="relative flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all duration-300 shadow-md hover:shadow-lg group flex-shrink-0"
                    title="Refresh products"
                  >
                    <RefreshCw
                      size={18}
                      className="text-gray-600 dark:text-gray-300 group-hover:rotate-180 transition-transform duration-500"
                    />
                    <span className="absolute inset-0 rounded-full bg-primary-500/0 group-hover:bg-primary-500/10 transition-all duration-300" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Error State */}
      {error && (
        <section className="pb-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 text-center"
            >
              <AlertCircle className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-700 dark:text-red-400 mb-2">
                {error}
              </h3>
              <button
                onClick={fetchProducts}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all inline-flex items-center gap-2"
              >
                <RefreshCw size={16} /> Try Again
              </button>
            </motion.div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            {/* Category Sidebar - Desktop */}
            <AnimatePresence>
              {isFilterVisible && (
                <motion.aside
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 280 }}
                  exit={{ opacity: 0, width: 0 }}
                  className="hidden lg:block flex-shrink-0"
                >
                  <div className="sticky top-24">
                    <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-800/50 p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          Categories
                        </h3>
                        {activeFiltersCount > 0 && (
                          <button
                            onClick={clearFilters}
                            className="text-sm text-primary-600 hover:text-primary-700"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                      <div className="space-y-3">
                        <button
                          onClick={() => setSelectedCategory("")}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                            selectedCategory === ""
                              ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border border-primary-500/20"
                              : "hover:bg-gray-100 dark:hover:bg-gray-800"
                          }`}
                        >
                          <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                            <FolderTree size={18} className="text-gray-500" />
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium text-gray-900 dark:text-white">
                              All Categories
                            </p>
                            <p className="text-xs text-gray-500">
                              View all products
                            </p>
                          </div>
                          {selectedCategory === "" && (
                            <div className="w-2 h-2 rounded-full bg-primary-500" />
                          )}
                        </button>

                        {categoryOptions.map((category) => {
                          const Icon = category.icon;
                          return (
                            <button
                              key={category.id}
                              onClick={() =>
                                setSelectedCategory(
                                  selectedCategory === category.id
                                    ? ""
                                    : category.id,
                                )
                              }
                              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                selectedCategory === category.id
                                  ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border border-primary-500/20"
                                  : "hover:bg-gray-100 dark:hover:bg-gray-800"
                              }`}
                            >
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                                <Icon size={18} className="text-white" />
                              </div>
                              <div className="flex-1 text-left">
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {category.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Browse {category.name.toLowerCase()}
                                </p>
                              </div>
                              {selectedCategory === category.id && (
                                <div className="w-2 h-2 rounded-full bg-primary-500" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </motion.aside>
              )}
            </AnimatePresence>

            {/* Products Container */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-800/50 p-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Showing{" "}
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {totalProducts > 0
                            ? (currentPage - 1) * productsPerPage + 1
                            : 0}
                          -
                          {Math.min(
                            currentPage * productsPerPage,
                            totalProducts,
                          )}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {totalProducts}
                        </span>{" "}
                        products
                      </p>
                    </div>
                    {activeFiltersCount > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          Active filters:
                        </span>
                        <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-xs font-medium rounded-full">
                          {activeFiltersCount}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="appearance-none pl-4 pr-10 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500/20 cursor-pointer"
                      >
                        <option value="popular">Most Popular</option>
                        <option value="newest">Newest First</option>
                        <option value="name">Name: A to Z</option>
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>

                    {/* View Toggle Buttons */}
                    <div className="flex items-center bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setViewMode("grid")}
                        className={`p-2 transition-colors ${
                          viewMode === "grid"
                            ? "bg-gradient-to-r from-primary-500 to-secondary-500 text-white"
                            : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                        title="Square Grid View"
                      >
                        <Grid size={18} />
                      </button>
                      <button
                        onClick={() => setViewMode("list")}
                        className={`p-2 transition-colors ${
                          viewMode === "list"
                            ? "bg-gradient-to-r from-primary-500 to-secondary-500 text-white"
                            : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                        title="Circular List View"
                      >
                        <Circle size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              )}

              {/* Products Display */}
              {!loading && products.length > 0 ? (
                <>
                  {viewMode === "grid" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {products.map((product) => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {products.map((product) => (
                        <ProductCircularCard
                          key={product.id}
                          product={product}
                        />
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6">
                      {/* Products per page selector */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          Show:
                        </span>
                        <select
                          value={productsPerPage}
                          onChange={(e) => {
                            setProductsPerPage(Number(e.target.value));
                            setCurrentPage(1); // Reset to first page when changing items per page
                          }}
                          className="appearance-none px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500/20 cursor-pointer"
                        >
                          <option value={9}>9 per page</option>
                          <option value={12}>12 per page</option>
                          <option value={24}>24 per page</option>
                          <option value={48}>48 per page</option>
                        </select>
                      </div>

                      {/* Page info */}
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Page{" "}
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {currentPage}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {totalPages}
                        </span>
                      </p>

                      {/* Pagination buttons */}
                      <div className="flex items-center gap-2">
                        {/* First page button */}
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="First page"
                        >
                          <ChevronLeft size={18} className="inline" />
                          <ChevronLeft size={18} className="inline -ml-3" />
                        </button>

                        {/* Previous page button */}
                        <button
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={currentPage === 1}
                          className="p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Previous page"
                        >
                          <ChevronLeft size={18} />
                        </button>

                        {/* Page numbers with ellipsis */}
                        {(() => {
                          const getPageNumbers = () => {
                            const pages = [];
                            const maxVisiblePages = 5;

                            if (totalPages <= maxVisiblePages + 2) {
                              // Show all pages if total is small
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              // Always show first page
                              pages.push(1);

                              if (currentPage > 3) {
                                pages.push("...");
                              }

                              // Calculate range around current page
                              let start = Math.max(2, currentPage - 1);
                              let end = Math.min(
                                totalPages - 1,
                                currentPage + 1,
                              );

                              // Adjust if at the beginning
                              if (currentPage <= 3) {
                                start = 2;
                                end = 4;
                              }

                              // Adjust if at the end
                              if (currentPage >= totalPages - 2) {
                                start = totalPages - 3;
                                end = totalPages - 1;
                              }

                              for (let i = start; i <= end; i++) {
                                pages.push(i);
                              }

                              if (currentPage < totalPages - 2) {
                                pages.push("...");
                              }

                              // Always show last page
                              pages.push(totalPages);
                            }

                            return pages;
                          };

                          return getPageNumbers().map((page, index) => {
                            if (page === "...") {
                              return (
                                <span
                                  key={`ellipsis-${index}`}
                                  className="w-10 h-10 flex items-center justify-center text-gray-400"
                                >
                                  ...
                                </span>
                              );
                            }

                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`w-10 h-10 rounded-xl font-medium transition-all ${
                                  currentPage === page
                                    ? "bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg"
                                    : "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:border-primary-500 hover:text-primary-600"
                                }`}
                              >
                                {page}
                              </button>
                            );
                          });
                        })()}

                        {/* Next page button */}
                        <button
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(prev + 1, totalPages),
                            )
                          }
                          disabled={currentPage === totalPages}
                          className="p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Next page"
                        >
                          <ChevronRight size={18} />
                        </button>

                        {/* Last page button */}
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className="p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Last page"
                        >
                          <ChevronRight size={18} className="inline" />
                          <ChevronRight size={18} className="inline -ml-3" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Quick jump to page */}
                  {totalPages > 10 && (
                    <div className="mt-4 flex justify-center items-center gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Go to page:
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={totalPages}
                        defaultValue={currentPage}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const page = parseInt(e.target.value);
                            if (page >= 1 && page <= totalPages) {
                              setCurrentPage(page);
                            }
                          }
                        }}
                        className="w-20 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                        placeholder="Page #"
                      />
                      <button
                        onClick={(e) => {
                          const input = e.target.previousElementSibling;
                          const page = parseInt(input.value);
                          if (page >= 1 && page <= totalPages) {
                            setCurrentPage(page);
                          }
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all"
                      >
                        Go
                      </button>
                    </div>
                  )}
                </>
              ) : (
                !loading && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-3xl border border-gray-200/50 dark:border-gray-800/50 p-16 text-center"
                  >
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Package size={48} className="text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                      No Products Found
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto">
                      We couldn't find any products matching your criteria. Try
                      adjusting your filters or search term.
                    </p>
                    <button
                      onClick={clearFilters}
                      className="px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-medium rounded-xl transition-all"
                    >
                      Clear All Filters
                    </button>
                  </motion.div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Category Panel */}
      <AnimatePresence>
        {showMobileFilters && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowMobileFilters(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween" }}
              className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white dark:bg-gray-900 shadow-xl overflow-y-auto"
            >
              <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-4 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Categories
                </h3>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <button
                  onClick={() => {
                    setSelectedCategory("");
                    setShowMobileFilters(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    selectedCategory === ""
                      ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border border-primary-500/20"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <Package size={18} className="text-gray-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white">
                      All Categories
                    </p>
                    <p className="text-xs text-gray-500">View all products</p>
                  </div>
                </button>

                {categoryOptions.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(
                          selectedCategory === category.id ? "" : category.id,
                        );
                        setShowMobileFilters(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        selectedCategory === category.id
                          ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border border-primary-500/20"
                          : "hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                        <Icon size={18} className="text-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {category.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          Browse {category.name.toLowerCase()}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Products;
